import { AssessmentInfo, EnabledProctoringOptions, VerityOptions } from "@verity/api-client/types";
import { CourseInfo, ExemptedStudent, Student, VerityInstituteSettings } from "../types/common";
import { createContext } from "react";
import { ApiClientError, ApiClientResponseError, WebApiClient } from "@verity/api-client";
import {
	Application,
	CreateProfileScope,
	Extension,
	ProctoringAssessmentConfigurationV2,
	ProctoringOption,
	ProctoringOptionProfileV2,
} from "@verity/proctoring-option/types";

export type AssessmentUuids = {
	instituteUuid: string;
	courseUuid: string;
	assessmentUuid: string;
};
export type LiveSessionUpdate = {
	name: string;
	startTime: Date;
	endTime: Date;
	capacity: number;
	registrationStartTime: Date | null;
	registrationEndTime: Date | null;
	proctorGuide?: string;
};
export type LiveSession = Awaited<ReturnType<typeof apiClient.liveClient.getLiveSession>>;

/**
 * Get the currently shared API client configuration.
 */
const apiClientPlatformConfig = {
	verityOrigin: window.location.origin,
	visualizerOrigin: import.meta.env.VITE_VISUALIZERS_S3_BUCKET_URL,
	lockdown: {
		origin: import.meta.env.VITE_LOCKDOWN_S3_BUCKET_URL,
		channel: import.meta.env.VITE_LOCKDOWN_CHANNEL,
		cdnBaseUrl: import.meta.env.VITE_LOCKDOWN_CDN_BASE_URL,
	},
	candidateAi: import.meta.env.VITE_CANDIDATE_AI_CHANNEL
		? { channel: import.meta.env.VITE_CANDIDATE_AI_CHANNEL }
		: undefined,
	moodlePlugin: import.meta.env.VITE_MOODLE_PLUGIN_CHANNEL
		? { channel: import.meta.env.VITE_MOODLE_PLUGIN_CHANNEL }
		: undefined,
};
const apiClient = WebApiClient.getInstance(apiClientPlatformConfig);

export interface VerityApi {
	getInstitute: typeof apiClient.institute.get;

	getInstituteIntegrated(this: void, hostname: string): Promise<boolean>;

	fetchVerityOptionsFromServer(this: void, quizInfo: AssessmentInfo): Promise<VerityOptions>;

	saveVeritySettings(
		this: void,
		options: {
			quizInfo: AssessmentInfo;
			courseInfo?: CourseInfo;
			options?: Partial<VerityOptions>;
			ignore?: Array<keyof VerityOptions>;
		},
	): Promise<void>;

	getLiveSetting(this: void): Promise<boolean>;

	getVerityInstituteSettings(this: void): Promise<VerityInstituteSettings>;

	getInstituteDisabledProctoringOptions(this: void): Promise<Array<keyof ProctoringOption>>;

	getInstituteAllowStrictMode(this: void): Promise<{
		allowAiStrictMode: boolean;
		allowStrictMode: boolean;
	}>;

	getEnabledProctoringOptions(
		this: void,
		query: { assessmentInfo: Omit<AssessmentInfo, "quizId">; courseName?: string },
	): Promise<EnabledProctoringOptions>;

	getCourseStudents(this: void, quizInfo: Pick<AssessmentInfo, "courseId">): Promise<Student[] | undefined>;

	getProctoringExemptionStudents(
		this: void,
		assessmentInfo: Pick<AssessmentInfo, "courseId" | "quizId">,
	): Promise<Array<Omit<ExemptedStudent, "name">>>;

	saveProctoringExemptionStudents(
		this: void,
		options: {
			assessmentInfo: Pick<AssessmentInfo, "courseId" | "quizId">;
			students: Array<Omit<ExemptedStudent, "name">>;
		},
	): Promise<void>;

	getExemptionInfoSuccessStatus(this: void, quizInfo: AssessmentInfo): Promise<boolean>;

	userLogout(this: void): Promise<void>;

	getProctoringAssessmentConfiguration(
		this: void,
		assessmentInfo: AssessmentInfo,
	): Promise<ProctoringAssessmentConfigurationV2 | null>;

	saveProctoringAssessmentConfiguration: typeof apiClient.proctoringConfiguration.save;

	getProctoringProfiles(
		this: void,
		options: {
			lmsProvidedCourseId?: string;
			includeSubAccountProfiles?: boolean;
			includeInstituteCourseProfiles?: boolean;
		},
	): Promise<Array<ProctoringOptionProfileV2>>;

	getPresetProfiles(this: void): Promise<Array<ProctoringOptionProfileV2>>;

	getInstituteScopeSettings: typeof apiClient.institute.getInstituteScopeSettings;

	createProctoringProfile(
		this: void,
		options: {
			proctoringOption: Partial<ProctoringOption>;
			isInstituteProfile?: boolean;
			lmsProvidedCourseId?: string;
			name?: string;
			scope?: CreateProfileScope;
			enforced?: boolean;
		},
	): Promise<ProctoringOptionProfileV2>;

	updateProctoringProfile(
		this: void,
		options: {
			id: number;
			proctoringOption: Partial<ProctoringOption>;
			name?: string;
			scope?: Extract<CreateProfileScope, { type: "subAccount" }>;
		},
	): Promise<ProctoringOptionProfileV2>;

	deleteProctoringProfile(this: void, id: number): Promise<void>;

	enforceProctoringProfiles(
		this: void,
		options: {
			enforcementEnabled?: boolean;
			enforceProfileIds?: Array<number>;
			unenforceProfileIds?: Array<number>;
		},
	): Promise<void>;

	areProctoringProfilesEnforced(this: void): Promise<boolean>;

	doesUserHaveInstituteProfilePermissions(this: void): Promise<boolean>;

	getDefaultProctoringProfile(this: void): Promise<ProctoringOptionProfileV2 | null>;

	setDefaultProctoringProfile(this: void, profileId: number | null): Promise<void>;

	getAllApplications(this: void): Promise<Application[]>;

	getAllExtensions(this: void): Promise<Extension[]>;

	fetchChromeExtensionData(this: void, id: string): Promise<Extension | null>;

	getSuffix(this: void): Promise<string | null>;

	getUserInstituteId(this: void): Promise<number>;

	getAssessmentByAssessmentInfo(
		this: void,
		assessmentInfo: AssessmentInfo,
	): ReturnType<typeof apiClient.assessment.getAssessmentByAssessmentInfo>;

	getCourseById(this: void, courseId: number): ReturnType<typeof apiClient.course.getCourseInfoFromId>;

	getCourseByLmsCourseId(
		this: void,
		courseInfo: {
			lmsCourseId: string;
			instituteId?: number;
		},
	): ReturnType<typeof apiClient.course.getCourseInfoFromLmsCourseId>;

	getCourseInstructors(
		this: void,
		lmsProvidedCourseId: string,
	): ReturnType<typeof apiClient.liveClient.getCourseLiveProctorInfo>;

	getInstituteProctorGroups(
		this: void,
		instituteId: string,
	): ReturnType<typeof apiClient.liveClient.getLiveInstituteProctorGroup>;

	getAssessmentLiveSessions(this: void, uuids: AssessmentUuids): Promise<{ results: LiveSession[] }>;

	getLiveSessionInvitations(
		this: void,
		liveSessionId: string,
	): ReturnType<typeof apiClient.liveClient.getLiveSessionInvitations>;

	createLiveSession(
		this: void,
		uuids: AssessmentUuids,
		liveSession: LiveSessionUpdate & {
			users?: {
				userId: string;
				role: "proctor" | "student";
			}[];
		},
	): Promise<LiveSession>;

	updateLiveSession(this: void, liveSessionId: string, update: Partial<LiveSessionUpdate>): Promise<LiveSession>;

	deleteLiveSession(this: void, liveSessionId: string): Promise<void>;

	addLiveSessionProctor(
		this: void,
		liveSessionId: string,
		userId: string,
	): ReturnType<typeof apiClient.liveClient.addLiveSessionUser>;

	removeLiveSessionProctor(this: void, liveSessionId: string, userId: string): Promise<void>;

	createProctorGroupInvitation(
		this: void,
		liveSessionId: string,
		proctorGroupId: number,
	): ReturnType<typeof apiClient.liveClient.createProctorGroupInvitation>;

	deleteProctorGroupInvitation(this: void, liveSessionId: string, proctorGroupId: number): Promise<void>;

	validateAssessment(
		this: void,
		{ courseId, quizId }: Pick<AssessmentInfo, "courseId" | "quizId">,
	): ReturnType<typeof apiClient.assessment.validate>;

	synchronizeAssessment(
		this: void,
		{ courseId, quizId }: Pick<AssessmentInfo, "courseId" | "quizId">,
	): ReturnType<typeof apiClient.assessment.synchronize>;

	getSetupGuideState: typeof apiClient.integration.getSetupGuideState;
	updateSetupGuideState: typeof apiClient.integration.updateSetupGuideState;
	getInstituteSettings: typeof apiClient.institute.getSettings;
	updateInstituteSettings: typeof apiClient.institute.updateSettings;
	getCourseSettings: typeof apiClient.course.getCourseSettings;
	getThirdPartyPlatforms: typeof apiClient.institute.getInstituteThirdPartyPlatforms;
	getVerityAssessmentByLmsQuizId: typeof apiClient.assessment.getVerityAssessmentByLmsQuizId;
	getAssessmentResources: typeof apiClient.proctoringConfiguration.getAssessmentResources;
	getReferenceMaterials: typeof apiClient.proctoringConfiguration.getReferenceMaterials;
	getReferenceMaterialUploadUrl: typeof apiClient.proctoringConfiguration.getReferenceMaterialUploadUrl;
	deleteAllReferenceMaterials: typeof apiClient.proctoringConfiguration.deleteAllReferenceMaterials;
	moveReferenceMaterialsFromTemp: typeof apiClient.proctoringConfiguration.moveReferenceMaterialsFromTemp;
	getAssessmentSubmissionUploadUrl: typeof apiClient.proctoringSession.getAssessmentSubmissionUploadUrl;
	getActiveSetupGuideStep: typeof apiClient.user.getActiveSetupGuideStep;
	setActiveSetupGuideStep: typeof apiClient.user.setActiveSetupGuideStep;
}

export const VerityApiContext = createContext({} as VerityApi);

export const api: VerityApi = {
	getInstitute: apiClient.institute.get,
	fetchVerityOptionsFromServer: apiClient.verityOptions.get,
	saveVeritySettings: apiClient.verityOptions.save,
	getInstituteIntegrated: apiClient.institute.isIntegrated,
	getLiveSetting: apiClient.institute.isLiveEnabled,
	getVerityInstituteSettings: apiClient.institute.getVeritySettings,
	getInstituteDisabledProctoringOptions: apiClient.institute.getInstituteDisabledProctoringOptions,
	getInstituteAllowStrictMode: apiClient.institute.getInstituteAllowStrictMode,
	getEnabledProctoringOptions: apiClient.proctoringConfiguration.getEnabledOptions,
	getCourseStudents: apiClient.user.getCourseStudents,
	getProctoringExemptionStudents: apiClient.user.getProctoringExemptionStudents,
	saveProctoringExemptionStudents: apiClient.user.saveProctoringExemptionStudents,
	getExemptionInfoSuccessStatus: apiClient.user.getExemptionInfoSuccessStatus,
	userLogout: apiClient.authentication.logout,
	getProctoringAssessmentConfiguration: async (...args) => {
		const configuration = await apiClient.proctoringConfiguration.get(...args);
		return configuration
			? {
					...configuration,
					forceChromeOsManagedGuestSession: !!configuration.forceChromeOsManagedGuestSession,
				}
			: configuration;
	},
	saveProctoringAssessmentConfiguration: apiClient.proctoringConfiguration.save,
	getProctoringProfiles: apiClient.proctoringProfile.getAll,
	getPresetProfiles: apiClient.proctoringProfile.getPreset,
	getInstituteScopeSettings: apiClient.institute.getInstituteScopeSettings,
	createProctoringProfile: apiClient.proctoringProfile.create,
	updateProctoringProfile: apiClient.proctoringProfile.update,
	deleteProctoringProfile: apiClient.proctoringProfile.delete,
	enforceProctoringProfiles: apiClient.proctoringProfile.enforce,
	areProctoringProfilesEnforced: apiClient.proctoringProfile.profilesEnforced,
	doesUserHaveInstituteProfilePermissions: apiClient.permission.doesUserHaveInstituteProfilePermissions,
	getDefaultProctoringProfile: apiClient.proctoringProfile.getDefault,
	setDefaultProctoringProfile: apiClient.proctoringProfile.setDefault,
	getAllApplications: apiClient.verityOptions.getAllApplications,
	getAllExtensions: apiClient.verityOptions.getAllExtensions,
	fetchChromeExtensionData: apiClient.verityOptions.fetchChromeExtensionData,
	getSuffix: apiClient.institute.getSuffix,
	getUserInstituteId: apiClient.liveClient.getStudentInstituteId,
	getCourseById: apiClient.course.getCourseInfoFromId,
	getCourseByLmsCourseId: apiClient.course.getCourseInfoFromLmsCourseId,
	getAssessmentByAssessmentInfo: apiClient.assessment.getAssessmentByAssessmentInfo,
	getAssessmentLiveSessions: (uuids: AssessmentUuids) =>
		apiClient.liveClient.getLiveSessionsWithUsers({
			...uuids,
			sortBy: "startTime",
		}),
	getCourseInstructors: apiClient.liveClient.getCourseLiveProctorInfo,
	getInstituteProctorGroups: apiClient.liveClient.getLiveInstituteProctorGroup,
	getLiveSessionInvitations: apiClient.liveClient.getLiveSessionInvitations,
	async createLiveSession(...args) {
		const result = await apiClient.liveClient.addLiveSession(...args);
		if (result.status === 413 || result.error || !result.updatedSession) {
			throw result.status ? new ApiClientResponseError(result.status, result.error ?? {}) : new ApiClientError();
		}
		return result.updatedSession;
	},
	async updateLiveSession(liveSessionId, update) {
		const result = await apiClient.liveClient.modifyLiveSession({ liveSessionId, payload: update });
		if (result.status === 413 || result.error || !result.updatedSession) {
			throw result.status ? new ApiClientResponseError(result.status, result.error ?? {}) : new ApiClientError();
		}
		return result.updatedSession;
	},
	async deleteLiveSession(liveSessionId) {
		const result = await apiClient.liveClient.deleteLiveSession({ liveSessionId });
		if (result.error) {
			throw result.status ? new ApiClientResponseError(result.status, result.error ?? {}) : new ApiClientError();
		}
	},
	addLiveSessionProctor(liveSessionId, userId) {
		return apiClient.liveClient.addLiveSessionUser({ liveSessionId, userId, role: "proctor" });
	},
	removeLiveSessionProctor: apiClient.liveClient.removeLiveSessionUser,
	createProctorGroupInvitation: apiClient.liveClient.createProctorGroupInvitation,
	deleteProctorGroupInvitation: apiClient.liveClient.deleteProctorGroupInvitation,
	validateAssessment: apiClient.assessment.validate,
	synchronizeAssessment: apiClient.assessment.synchronize,
	getSetupGuideState: apiClient.integration.getSetupGuideState,
	updateSetupGuideState: apiClient.integration.updateSetupGuideState,
	getInstituteSettings: apiClient.institute.getSettings,
	updateInstituteSettings: apiClient.institute.updateSettings,
	getCourseSettings: apiClient.course.getCourseSettings,
	getThirdPartyPlatforms: apiClient.institute.getInstituteThirdPartyPlatforms,
	getVerityAssessmentByLmsQuizId: apiClient.assessment.getVerityAssessmentByLmsQuizId,
	getAssessmentResources: apiClient.proctoringConfiguration.getAssessmentResources,
	getReferenceMaterials: apiClient.proctoringConfiguration.getReferenceMaterials,
	getReferenceMaterialUploadUrl: apiClient.proctoringConfiguration.getReferenceMaterialUploadUrl,
	deleteAllReferenceMaterials: apiClient.proctoringConfiguration.deleteAllReferenceMaterials,
	moveReferenceMaterialsFromTemp: apiClient.proctoringConfiguration.moveReferenceMaterialsFromTemp,
	getAssessmentSubmissionUploadUrl: apiClient.proctoringSession.getAssessmentSubmissionUploadUrl,
	getActiveSetupGuideStep: apiClient.user.getActiveSetupGuideStep,
	setActiveSetupGuideStep: apiClient.user.setActiveSetupGuideStep,
};
